// BatchDetailPage.jsx - Part 1: Import statements, state, and core functions
import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeftIcon,
  EyeIcon,
  ArrowPathIcon,
  MagnifyingGlassIcon,
  PhotoIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  ClockIcon,
  XCircleIcon,
  Bars3Icon,
  ArrowUpIcon,
  ArrowDownIcon,
  ArrowsUpDownIcon,
  DocumentArrowUpIcon,
  ChevronDoubleUpIcon,
  ChevronDoubleDownIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors
} from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
  arrayMove,
  useSortable,
  sortableKeyboardCoordinates
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { batchAPI, imageAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import toast from 'react-hot-toast';
import ImagePreview, { ProcessingImagePlaceholder } from '../components/ImagePreview';
import Cookies from 'js-cookie';

// Status/review pills shown on an image card
const ReviewPills = ({ image, className = '' }) => {
  const review = image.review || {};
  const conf = review.confidence;
  const reasons = (review.reasons || []).map(r => r.label).join(', ');
  const pill = 'inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium';

  const hasAny =
    conf || review.recommendedForReview || image.manually_edited || review.reviewed;
  if (!hasAny) return null;

  return (
    <div className={`flex flex-wrap gap-1.5 ${className}`}>
      {conf === 'low' && <span className={`${pill} bg-red-100 text-red-800`}>Low confidence</span>}
      {conf === 'medium' && <span className={`${pill} bg-amber-50 text-amber-700`}>Medium confidence</span>}
      {conf === 'high' && <span className={`${pill} bg-green-100 text-green-800`}>High confidence</span>}
      {review.recommendedForReview && (
        <span
          className={`${pill} bg-amber-100 text-amber-800`}
          title={reasons ? `Reasons: ${reasons}` : undefined}
        >
          Recommended for Review
        </span>
      )}
      {image.manually_edited ? (
        <span className={`${pill} bg-indigo-100 text-indigo-800`}>Manually updated</span>
      ) : null}
      {review.reviewed && <span className={`${pill} bg-green-100 text-green-800`}>Reviewed</span>}
    </div>
  );
};

const BatchDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  // State management
  const [batch, setBatch] = useState(null);
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [imagesLoading, setImagesLoading] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [showImageModal, setShowImageModal] = useState(false);
  const [showReprocessModal, setShowReprocessModal] = useState(false);
  const [reprocessingImages, setReprocessingImages] = useState(new Set());

  // Sorting
  const [sortMode, setSortMode] = useState('alphabetical');
  const [showArrangeModal, setShowArrangeModal] = useState(false);
  
  // Filters and pagination
  const [statusFilter, setStatusFilter] = useState('');
  const [flagFilter, setFlagFilter] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0,
    hasNext: false,
    hasPrev: false
  });

  // Polling for status updates
  const pollIntervalRef = useRef(null);

  // Load initial data (using the working approach from debug version)
  const loadData = async () => {
    try {
      setLoading(true);
      
      // Load batch details
      const batchResponse = await batchAPI.get(id);
      const loadedBatch = batchResponse.data.data.batch;
      setBatch(loadedBatch);
      setSortMode(loadedBatch.sort_mode || 'alphabetical');

      // Load images
      await loadImages();
      
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Failed to load batch details');
      navigate('/batches');
    } finally {
      setLoading(false);
    }
  };

  // Load images with filters and pagination (simplified working version)
  const loadImages = async (page = 1, status = statusFilter, flag = flagFilter) => {
    try {
      setImagesLoading(true);

      const queryParams = {
        page: page.toString(),
        limit: pagination.limit.toString()
      };

      if (status && status.trim()) {
        queryParams.status = status.trim();
      }
      if (flag && flag.trim()) {
        queryParams.flag = flag.trim();
      }

      const response = await batchAPI.getImages(id, queryParams);
      
      if (response.data && response.data.data) {
        setImages(response.data.data.images || []);
        setPagination(response.data.data.pagination || {
          page: 1,
          limit: 20,
          total: 0,
          totalPages: 0,
          hasNext: false,
          hasPrev: false
        });
      }
    } catch (error) {
      console.error('Error loading images:', error);
      toast.error('Failed to load images');
    } finally {
      setImagesLoading(false);
    }
  };

  // Quiet refresh for polling (continues from Part 1)
  const refreshImagesQuietly = async () => {
    try {
      // Refresh both images AND batch details
      const [imagesResponse, batchResponse] = await Promise.all([
        batchAPI.getImages(id, {
          page: pagination.page.toString(),
          limit: pagination.limit.toString(),
          ...(statusFilter && statusFilter.trim() ? { status: statusFilter.trim() } : {}),
          ...(flagFilter && flagFilter.trim() ? { flag: flagFilter.trim() } : {})
        }),
        batchAPI.get(id)
      ]);
      
      // Update images if changed
      if (imagesResponse.data && imagesResponse.data.data) {
        const newImages = imagesResponse.data.data.images || [];
        setImages(prevImages => {
          const hasChanged = JSON.stringify(prevImages) !== JSON.stringify(newImages);
          return hasChanged ? newImages : prevImages;
        });
      }

      // Update batch details if changed
      if (batchResponse.data && batchResponse.data.data) {
        const newBatch = batchResponse.data.data.batch;
        setBatch(prevBatch => {
          const hasChanged = JSON.stringify(prevBatch) !== JSON.stringify(newBatch);
          return hasChanged ? newBatch : prevBatch;
        });
      }
    } catch (error) {
      console.error('Quiet refresh error:', error);
    }
  };

  // Initial load
  useEffect(() => {
    if (id) {
      loadData();
    }
  }, [id]);

  // Handle filter changes
  useEffect(() => {
    if (!loading) {
      loadImages(1, statusFilter, flagFilter);
    }
  }, [statusFilter, flagFilter]);

  // Polling management
  useEffect(() => {
    const hasActiveImages = images.some(image => 
      image.processing_status === 'processing' || 
      image.processing_status === 'queued' || 
      image.processing_status === 'reprocessing'
    );

    if (hasActiveImages && !pollIntervalRef.current) {
      pollIntervalRef.current = setInterval(refreshImagesQuietly, 10000);
    } else if (!hasActiveImages && pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }

    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
    };
  }, [images]);

  // Event handlers
  const handlePageChange = (newPage) => {
    loadImages(newPage, statusFilter, flagFilter);
  };

  // Handle image updates from modal
  const handleImageUpdate = (imageId, updatedFields) => {
    setImages(prevImages => 
      prevImages.map(img => 
        img.id === imageId 
          ? { ...img, ...updatedFields }
          : img
      )
    );
  };

  // Sorting handlers
  const handleSelectAlphabetical = async () => {
    if (sortMode === 'alphabetical') return;
    try {
      await batchAPI.updateSort(id, { mode: 'alphabetical' });
      setSortMode('alphabetical');
      setBatch(prev => (prev ? { ...prev, sort_mode: 'alphabetical' } : prev));
      toast.success('Sorted alphabetically');
      await loadImages(1, statusFilter);
    } catch (error) {
      toast.error('Failed to update sort order');
    }
  };

  // Re-sync sort state + images from the server after the arrange modal closes
  const refreshAfterArrange = async () => {
    try {
      const batchResponse = await batchAPI.get(id);
      const b = batchResponse.data.data.batch;
      setBatch(b);
      setSortMode(b.sort_mode || 'alphabetical');
      await loadImages(1, statusFilter);
    } catch (error) {
      console.error('Error refreshing after arrange:', error);
    }
  };

  const handleArrangeClose = async () => {
    setShowArrangeModal(false);
    await refreshAfterArrange();
  };

  // Refresh just the batch object (stats + flag counts) — used after a review toggle.
  const refreshBatchStats = async () => {
    try {
      const batchResponse = await batchAPI.get(id);
      setBatch(batchResponse.data.data.batch);
    } catch (error) {
      console.error('Error refreshing batch stats:', error);
    }
  };

  const handleReprocessImage = async (imageId, additionalComments) => {
    try {
      setReprocessingImages(prev => new Set([...prev, imageId]));
      
      // Optimistically update the image status to reprocessing immediately
      handleImageUpdate(imageId, { 
        processing_status: 'reprocessing',
        error_message: null 
      });
      
      await imageAPI.reprocess(imageId, { additional_comments: additionalComments });
      
      toast.success('Image added to reprocessing queue');
      setShowReprocessModal(false);
      setSelectedImage(null);
      
      // Refresh both batch details and images after a short delay to get server state
      setTimeout(async () => {
        try {
          // Load fresh batch details
          const batchResponse = await batchAPI.get(id);
          setBatch(batchResponse.data.data.batch);
          
          // Load fresh images
          await loadImages(pagination.page, statusFilter, flagFilter);
        } catch (error) {
          console.error('Error refreshing data after reprocess:', error);
        }
      }, 500); // Reduced delay since we optimistically updated
      
    } catch (error) {
      // Revert optimistic update on error
      handleImageUpdate(imageId, { processing_status: 'failed' });
      toast.error(error.response?.data?.message || 'Failed to reprocess image');
    } finally {
      setReprocessingImages(prev => {
        const newSet = new Set(prev);
        newSet.delete(imageId);
        return newSet;
      });
    }
  };

  // Helper functions
  const getStatusDisplay = (status) => {
    switch (status) {
      case 'completed':
        return {
          color: 'text-green-600 bg-green-50',
          icon: CheckCircleIcon,
          text: 'Completed'
        };
      case 'processing':
      case 'reprocessing':
        return {
          color: 'text-blue-600 bg-blue-50',
          icon: ClockIcon,
          text: status === 'reprocessing' ? 'Reprocessing' : 'Processing'
        };
      case 'queued':
        return {
          color: 'text-yellow-600 bg-yellow-50',
          icon: ClockIcon,
          text: 'Queued'
        };
      case 'failed':
        return {
          color: 'text-red-600 bg-red-50',
          icon: XCircleIcon,
          text: 'Failed'
        };
      case 'pending':
        return {
          color: 'text-gray-600 bg-gray-50',
          icon: ClockIcon,
          text: 'Pending'
        };
      default:
        return {
          color: 'text-gray-600 bg-gray-50',
          icon: ClockIcon,
          text: status
        };
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Filter images by search term (client-side for now)
  const filteredImages = images.filter(image => {
    if (!searchTerm.trim()) return true;
    return image.filename.toLowerCase().includes(searchTerm.toLowerCase()) ||
           (image.alt_text && image.alt_text.toLowerCase().includes(searchTerm.toLowerCase()));
  });

  // Loading and error states (continues from Part 2)
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-64">
        <div className="w-8 h-8 loading-spinner"></div>
      </div>
    );
  }

  if (!batch) {
    return (
      <div className="py-12 text-center">
        <h3 className="mb-2 text-lg font-medium text-gray-900">Batch not found</h3>
        <button onClick={() => navigate('/batches')} className="btn-primary">
          Back to Batches
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate('/batches')}
            className="flex items-center btn-outline btn-sm"
          >
            <ArrowLeftIcon className="w-4 h-4 mr-2" />
            Back to Batches
          </button>
          <div className="flex items-center space-x-3">
            <div className="flex justify-center items-center w-10 h-10 bg-blue-100 rounded-lg">
              <PhotoIcon className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">{batch.name}</h1>
              <div className="flex items-center mt-1 space-x-4 text-sm text-gray-500">
                <span>Language: {batch.language}</span>
                <span>•</span>
                <span>Length: {batch.alt_text_length}</span>
                <span>•</span>
                <span>Created: {formatDate(batch.created_at)}</span>
              </div>
            </div>
          </div>
        </div>
        
        <div className="flex items-center space-x-3">
          <span className={`px-3 py-1 text-sm font-medium rounded-full ${
            batch.status === 'completed' ? 'bg-green-100 text-green-800' :
            batch.status === 'processing' ? 'bg-blue-100 text-blue-800' :
            batch.status === 'queued' ? 'bg-yellow-100 text-yellow-800' :
            batch.status === 'failed' ? 'bg-red-100 text-red-800' :
            'bg-gray-100 text-gray-800'
          }`}>
            {batch.status.charAt(0).toUpperCase() + batch.status.slice(1)}
          </span>
        </div>
      </div>

      {/* Comments */}
      {batch.comments && (
        <div className="card">
          <div className="pt-4 card-content">
            <h3 className="mb-2 text-sm font-medium text-gray-900">Comments</h3>
            <p className="text-sm text-gray-600">{batch.comments}</p>
          </div>
        </div>
      )}

{/* Stats Summary */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-3 lg:grid-cols-6">
        <div className="card">
          <div className="pt-4 text-center card-content">
            <div className="text-2xl font-bold text-gray-900">{batch.total_images || 0}</div>
            <div className="text-sm text-gray-500">Total Images</div>
          </div>
        </div>
        <div className="card">
          <div className="pt-4 text-center card-content">
            <div className="text-2xl font-bold text-green-600">{batch.successful_images || 0}</div>
            <div className="text-sm text-gray-500">Completed</div>
          </div>
        </div>
        <div className="card">
          <div className="pt-4 text-center card-content">
            <div className="text-2xl font-bold text-red-600">{batch.failed_images || 0}</div>
            <div className="text-sm text-gray-500">Failed</div>
          </div>
        </div>
        <div className="card">
          <div className="pt-4 text-center card-content">
            <div className="text-2xl font-bold text-blue-600">{batch.processing_images || 0}</div>
            <div className="text-sm text-gray-500">Processing</div>
          </div>
        </div>
        <div className="card">
          <div className="pt-4 text-center card-content">
            <div className="text-2xl font-bold text-yellow-600">{batch.queued_images || 0}</div>
            <div className="text-sm text-gray-500">Queued</div>
          </div>
        </div>
        <div className="card">
          <div className="pt-4 text-center card-content">
            <div className="text-2xl font-bold text-amber-600">{batch.recommended_for_review || 0}</div>
            <div className="text-sm text-gray-500">For Review</div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="card">
        <div className="pt-6 card-content">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
            <div className="flex-1">
              <div className="relative">
                <MagnifyingGlassIcon className="absolute w-5 h-5 text-gray-400 left-3 top-3" />
                <input
                  type="text"
                  placeholder="Search images by filename or alt-text..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 input"
                />
              </div>
            </div>
            <div className="sm:w-48">
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="input"
              >
                <option value="">All Statuses</option>
                <option value="pending">Pending</option>
                <option value="queued">Queued</option>
                <option value="processing">Processing</option>
                <option value="reprocessing">Reprocessing</option>
                <option value="completed">Completed</option>
                <option value="failed">Failed</option>
              </select>
            </div>
            <div className="sm:w-52">
              <select
                value={flagFilter}
                onChange={(e) => setFlagFilter(e.target.value)}
                className="input"
              >
                <option value="">All Flags</option>
                <option value="recommended">Recommended for Review ({batch.flag_counts?.recommended ?? 0})</option>
                <option value="high">High confidence ({batch.flag_counts?.high ?? 0})</option>
                <option value="medium">Medium confidence ({batch.flag_counts?.medium ?? 0})</option>
                <option value="low">Low confidence ({batch.flag_counts?.low ?? 0})</option>
                <option value="manual">Manually updated ({batch.flag_counts?.manual ?? 0})</option>
                <option value="reviewed">Reviewed ({batch.flag_counts?.reviewed ?? 0})</option>
              </select>
            </div>
            {/* Sort order */}
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-500 whitespace-nowrap">Sort:</span>
              <div className="inline-flex rounded-md shadow-sm">
                <button
                  onClick={handleSelectAlphabetical}
                  title="Order alphabetically by file name"
                  className={`px-3 py-2 text-sm border rounded-l-md whitespace-nowrap ${
                    sortMode === 'alphabetical'
                      ? 'bg-blue-600 text-white border-blue-600'
                      : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  A–Z
                </button>
                <button
                  onClick={() => setShowArrangeModal(true)}
                  title="Arrange a custom order (also used in downloads)"
                  className={`-ml-px px-3 py-2 text-sm border rounded-r-md whitespace-nowrap ${
                    sortMode === 'custom'
                      ? 'bg-blue-600 text-white border-blue-600'
                      : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  Custom
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Images Grid (continues from Part 3) */}
      <div className="card">
        <div className="pt-6 card-content">
          {imagesLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="w-6 h-6 loading-spinner"></div>
            </div>
          ) : filteredImages.length === 0 ? (
            <div className="py-12 text-center">
              <PhotoIcon className="w-12 h-12 mx-auto text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">No images found</h3>
              <p className="mt-1 text-sm text-gray-500">
                {statusFilter || searchTerm ? 'Try adjusting your filters' : 'Upload some images to get started'}
              </p>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 gap-4 lg:grid-cols-2 xl:grid-cols-3">
                {filteredImages.map((image) => {
                  const statusDisplay = getStatusDisplay(image.processing_status);
                  const StatusIcon = statusDisplay.icon;
                  
                  return (
                    <div key={image.id} className="flex flex-col h-full p-4 transition-shadow border border-gray-200 rounded-lg hover:shadow-md">
                      {/* Image Preview */}
                      <div className="mb-3">
                        <div className="relative w-full h-32 overflow-hidden bg-gray-100 rounded-md">
                          {image.processing_status === 'pending' || 
                           image.processing_status === 'queued' || 
                           image.processing_status === 'processing' || 
                           image.processing_status === 'reprocessing' ? (
                            // Show processing placeholder for images being processed
                            <ProcessingImagePlaceholder status={image.processing_status} />
                          ) : (
                            // Show image preview for completed/failed images
                            <ImagePreview 
                              batchId={batch.id} 
                              image={image} 
                              altText={image.alt_text || 'Image preview'} 
                            />
                          )}
                        </div>
                      </div>

                      <div className="flex-grow">
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex-1 min-w-0">
                            <h4 className="text-sm font-medium text-gray-900 truncate" title={image.filename}>
                              {image.filename}
                            </h4>
                            <div className="flex items-center mt-1">
                              <StatusIcon className="w-4 h-4 mr-1" />
                              <span className={`text-xs px-2 py-1 rounded-full font-medium ${statusDisplay.color}`}>
                                {statusDisplay.text}
                              </span>
                            </div>
                          </div>
                        </div>

                        {/* Review pills */}
                        <ReviewPills image={image} className="mb-3" />

                        {/* Alt Text Preview */}
                        {image.alt_text && (
                          <div className="mb-3">
                            <p className="text-xs text-gray-600 line-clamp-3">
                              {image.alt_text}
                            </p>
                          </div>
                        )}

                        {/* Error Message */}
                        {image.processing_status === 'failed' && image.error_message && (
                          <div className="mb-3">
                            <div className="flex items-start">
                              <ExclamationTriangleIcon className="h-4 w-4 text-red-500 mr-2 mt-0.5 flex-shrink-0" />
                              <p className="text-xs text-red-600">{image.error_message}</p>
                            </div>
                          </div>
                        )}

                        {/* Metadata */}
                        <div className="mb-3 space-y-1 text-xs text-gray-500">
                          <div>Uploaded: {formatDate(image.created_at)}</div>
                          {image.processed_at && (
                            <div>Processed: {formatDate(image.processed_at)}</div>
                          )}
                          {image.reprocess_count > 0 && (
                            <div>Reprocessed: {image.reprocess_count} times</div>
                          )}
                          {/* ONLY SHOW FOR ADMIN */}
                          {user?.role === 'admin' && image.tokens_used > 0 && (
                            <div>Tokens: {image.tokens_used} • Cost: ${parseFloat(image.cost || 0).toFixed(4)}</div>
                          )}
                        </div>
                      </div>

                      {/* Actions - Now will always be at bottom */}
                      <div className="flex items-center justify-between pt-3 mt-auto">
                        <button
                          onClick={() => {
                            setSelectedImage(image);
                            setShowImageModal(true);
                          }}
                          className="flex items-center btn-outline btn-sm"
                        >
                          <EyeIcon className="w-4 h-4 mr-1" />
                          View Details
                        </button>

                        {(image.processing_status === 'completed' || image.processing_status === 'failed') && (
                          <button
                            onClick={() => {
                              setSelectedImage(image);
                              setShowReprocessModal(true);
                            }}
                            disabled={reprocessingImages.has(image.id)}
                            className="flex items-center btn-primary btn-sm"
                          >
                            {reprocessingImages.has(image.id) ? (
                              <>
                                <div className="w-4 h-4 mr-1 animate-spin">
                                  <ClockIcon className="w-4 h-4" />
                                </div>
                                Queuing...
                              </>
                            ) : (
                              <>
                                <ArrowPathIcon className="w-4 h-4 mr-1" />
                                Reprocess
                              </>
                            )}
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Pagination (continues from Part 4) */}
              {pagination.totalPages > 1 && (
                <div className="flex items-center justify-between mt-6">
                  <div className="text-sm text-gray-700">
                    Showing {((pagination.page - 1) * pagination.limit) + 1} to {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total} images
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => handlePageChange(pagination.page - 1)}
                      disabled={!pagination.hasPrev}
                      className="px-3 py-2 text-sm border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                    >
                      Previous
                    </button>

                    <div className="flex items-center space-x-1">
                      {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                        let pageNum;
                        if (pagination.totalPages <= 5) {
                          pageNum = i + 1;
                        } else if (pagination.page <= 3) {
                          pageNum = i + 1;
                        } else if (pagination.page >= pagination.totalPages - 2) {
                          pageNum = pagination.totalPages - 4 + i;
                        } else {
                          pageNum = pagination.page - 2 + i;
                        }

                        return (
                          <button
                            key={pageNum}
                            onClick={() => handlePageChange(pageNum)}
                            className={`px-3 py-2 text-sm border rounded-md ${
                              pageNum === pagination.page
                                ? 'bg-blue-600 text-white border-blue-600'
                                : 'border-gray-300 hover:bg-gray-50'
                            }`}
                          >
                            {pageNum}
                          </button>
                        );
                      })}
                    </div>

                    <button
                      onClick={() => handlePageChange(pagination.page + 1)}
                      disabled={!pagination.hasNext}
                      className="px-3 py-2 text-sm border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                    >
                      Next
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Modal Components */}
      {showImageModal && selectedImage && (
        <ImageDetailModal
          image={selectedImage}
          onClose={() => {
            setShowImageModal(false);
            setSelectedImage(null);
          }}
          onReprocess={() => {
            setShowImageModal(false);
            setShowReprocessModal(true);
          }}
          onImageUpdate={handleImageUpdate}
          onReviewChanged={refreshBatchStats}
        />
      )}

      {showReprocessModal && selectedImage && (
        <ReprocessModal
          image={selectedImage}
          onClose={() => {
            setShowReprocessModal(false);
            setSelectedImage(null);
          }}
          onReprocess={handleReprocessImage}
        />
      )}

      {showArrangeModal && (
        <ArrangeOrderModal
          batchId={id}
          onClose={handleArrangeClose}
          onSaved={handleArrangeClose}
        />
      )}
    </div>
  );
};

// Image Detail Modal Component (continues from Part 5)
const ImageDetailModal = ({ image, onClose, onReprocess, onImageUpdate, onReviewChanged }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [showFullPreview, setShowFullPreview] = useState(false);
  const [isEditingAltText, setIsEditingAltText] = useState(false);
  const [editedAltText, setEditedAltText] = useState(image.alt_text || '');
  const [isSaving, setIsSaving] = useState(false);
  const [reviewSaving, setReviewSaving] = useState(false);
  const [textSize, setTextSize] = useState('small'); // small, medium, or large
  const { user } = useAuth();
  const { id: batchId } = useParams(); // Get batchId from URL params

  const isReviewed = !!image.reviewed_at || !!(image.review && image.review.reviewed);

  const handleToggleReviewed = async () => {
    setReviewSaving(true);
    try {
      const next = !isReviewed;
      await imageAPI.setReviewStatus(image.id, next);
      const reviewed_at = next ? new Date().toISOString() : null;
      image.reviewed_at = reviewed_at;
      image.review = { ...(image.review || {}), reviewed: next };
      toast.success(next ? 'Marked as reviewed' : 'Review mark cleared');
      if (onImageUpdate) {
        onImageUpdate(image.id, { reviewed_at, review: image.review });
      }
      if (onReviewChanged) {
        onReviewChanged();
      }
    } catch (error) {
      toast.error('Failed to update review status');
    } finally {
      setReviewSaving(false);
    }
  };

  useEffect(() => {
    setIsVisible(true);
    // Prevent body scroll when modal is open
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, []);

  // Update edited alt text when image changes
  useEffect(() => {
    setEditedAltText(image.alt_text || '');
    setIsEditingAltText(false);
  }, [image.alt_text]);

  const handleSaveAltText = async () => {
    if (!editedAltText.trim()) {
      toast.error('Alt text cannot be empty');
      return;
    }

    if (editedAltText.trim() === image.alt_text) {
      setIsEditingAltText(false);
      return;
    }

    setIsSaving(true);
    try {
      await imageAPI.updateAltText(image.id, { alt_text: editedAltText.trim() });

      // Update the image object locally
      image.alt_text = editedAltText.trim();
      image.manually_edited = true;

      toast.success('Alt text updated successfully');
      setIsEditingAltText(false);

      // Update the parent component if callback is provided
      if (onImageUpdate) {
        onImageUpdate(image.id, { alt_text: editedAltText.trim(), manually_edited: true });
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to update alt text');
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancelEdit = () => {
    setEditedAltText(image.alt_text || '');
    setIsEditingAltText(false);
  };

  const handleClose = () => {
    setIsVisible(false);
    setTimeout(() => {
      document.body.style.overflow = 'unset';
      onClose();
    }, 300);
  };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  // Parse tags safely
  const getTags = () => {
    if (!image.tags) return [];
    try {
      if (typeof image.tags === 'string') {
        return JSON.parse(image.tags);
      }
      return Array.isArray(image.tags) ? image.tags : [];
    } catch (e) {
      return [];
    }
  };

  const tags = getTags();

  return (
    <>
      <div 
        className={`fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 transition-opacity duration-300 ${
          isVisible ? 'opacity-100' : 'opacity-0'
        }`}
        style={{ 
          zIndex: 9999,
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          margin: 0
        }}
        onClick={handleBackdropClick}
      >
        <div 
          className={`bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-auto transform transition-all duration-300 ${
            isVisible ? 'scale-100 translate-y-0' : 'scale-95 translate-y-4'
          }`}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-medium text-gray-900">Image Details</h3>
              <button
                onClick={handleClose}
                className="text-gray-400 hover:text-gray-500"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
              {/* Left Column: Image */}
              <div>
                <h4 className="mb-3 text-sm font-medium text-gray-900">Image Preview</h4>
                <div 
                  className="w-full overflow-hidden bg-gray-100 rounded-lg cursor-pointer h-80"
                  onClick={() => setShowFullPreview(true)}
                >
                  {image.processing_status === 'pending' || 
                   image.processing_status === 'queued' || 
                   image.processing_status === 'processing' || 
                   image.processing_status === 'reprocessing' ? (
                    <ProcessingImagePlaceholder status={image.processing_status} />
                  ) : (
                    <ImagePreview 
                      batchId={batchId} 
                      image={image} 
                      altText={image.alt_text || 'Full size image preview'} 
                      className="object-contain w-full h-full" // Use object-contain for full view
                    />
                  )}
                </div>
                
                {/* Image Info */}
                <div className="mt-3 text-sm text-gray-500">
                  <p><strong>Filename:</strong> {image.filename}</p>
                  {user?.role === 'admin' && (
                    <>
                      {image.tokens_used > 0 && (
                        <p><strong>Tokens Used:</strong> {image.tokens_used}</p>
                      )}
                      {image.cost > 0 && (
                        <p><strong>Cost:</strong> ${parseFloat(image.cost || 0).toFixed(4)}</p>
                      )}
                    </>
                  )}
                  {image.reprocess_count > 0 && (
                    <p><strong>Reprocessed:</strong> {image.reprocess_count} times</p>
                  )}
                </div>
              </div>

              {/* Right Column: Details */}
              <div className="space-y-6">
                {/* Status */}
                <div>
                  <h4 className="mb-2 text-sm font-medium text-gray-900">Status</h4>
                  <span className={`inline-flex px-3 py-1 text-sm font-medium rounded-full ${
                    image.processing_status === 'completed' ? 'bg-green-100 text-green-800' :
                    image.processing_status === 'processing' ? 'bg-blue-100 text-blue-800' :
                    image.processing_status === 'reprocessing' ? 'bg-purple-100 text-purple-800' :
                    image.processing_status === 'queued' ? 'bg-yellow-100 text-yellow-800' :
                    image.processing_status === 'failed' ? 'bg-red-100 text-red-800' :
                    'bg-gray-100 text-gray-800'
                  }`}>
                    {image.processing_status.charAt(0).toUpperCase() + image.processing_status.slice(1)}
                  </span>
                </div>

                {/* Review */}
                <div>
                  <h4 className="mb-2 text-sm font-medium text-gray-900">Review</h4>
                  <div className="flex flex-wrap items-center gap-2">
                    {image.review?.confidence ? (
                      <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${
                        image.review.confidence === 'low' ? 'bg-red-100 text-red-800' :
                        image.review.confidence === 'medium' ? 'bg-amber-50 text-amber-700' :
                        'bg-green-100 text-green-800'
                      }`}>
                        {image.review.confidence.charAt(0).toUpperCase() + image.review.confidence.slice(1)} confidence
                      </span>
                    ) : (
                      <span className="text-xs text-gray-400">No confidence rating</span>
                    )}
                    {image.manually_edited ? (
                      <span className="inline-flex px-2 py-0.5 text-xs font-medium text-indigo-800 bg-indigo-100 rounded-full">Manually updated</span>
                    ) : null}
                    {isReviewed && (
                      <span className="inline-flex px-2 py-0.5 text-xs font-medium text-green-800 bg-green-100 rounded-full">Reviewed</span>
                    )}
                  </div>
                  {image.review?.recommendedForReview && (
                    <div className="mt-2 text-xs text-amber-700">
                      Recommended for review — {(image.review.reasons || []).map(r => r.label).join(', ')}
                    </div>
                  )}
                  <button
                    onClick={handleToggleReviewed}
                    disabled={reviewSaving}
                    className="mt-3 btn-outline btn-sm"
                  >
                    {reviewSaving ? 'Saving…' : (isReviewed ? 'Unmark reviewed' : 'Mark as reviewed')}
                  </button>
                </div>

                {/* Alt Text */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="text-sm font-medium text-gray-900">
                      {image.alt_text ? 'Generated Alt Text' : 'Alt Text'}
                    </h4>
                    <div className="flex items-center space-x-3">
                      {/* Text Size Controls - only show when not editing and alt text exists */}
                      {!isEditingAltText && image.alt_text && (
                        <div className="flex items-center space-x-1">
                          <span className="text-xs text-gray-500">Text size:</span>
                          <button
                            onClick={() => setTextSize('small')}
                            className={`px-2 py-1 text-xs font-medium rounded transition-colors ${
                              textSize === 'small' 
                                ? 'bg-blue-100 text-blue-700 border border-blue-200' 
                                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                            }`}
                            title="Small text size"
                          >
                            S
                          </button>
                          <button
                            onClick={() => setTextSize('medium')}
                            className={`px-2 py-1 text-xs font-medium rounded transition-colors ${
                              textSize === 'medium' 
                                ? 'bg-blue-100 text-blue-700 border border-blue-200' 
                                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                            }`}
                            title="Medium text size"
                          >
                            M
                          </button>
                          <button
                            onClick={() => setTextSize('large')}
                            className={`px-2 py-1 text-xs font-medium rounded transition-colors ${
                              textSize === 'large' 
                                ? 'bg-blue-100 text-blue-700 border border-blue-200' 
                                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                            }`}
                            title="Large text size"
                          >
                            L
                          </button>
                        </div>
                      )}
                      {!isEditingAltText && image.alt_text && (
                        <span className="text-gray-300">|</span>
                      )}
                      {!isEditingAltText && (
                        <button
                          onClick={() => setIsEditingAltText(true)}
                          className="text-sm text-blue-600 hover:text-blue-800"
                        >
                          {image.alt_text ? 'Edit' : 'Add Alt Text'}
                        </button>
                      )}
                    </div>
                  </div>
                    
                    {isEditingAltText ? (
                      <div className="space-y-3">
                        <textarea
                          value={editedAltText}
                          onChange={(e) => setEditedAltText(e.target.value)}
                          rows={4}
                          className={`w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                            textSize === 'small' ? 'text-sm' : 
                            textSize === 'medium' ? 'text-base' : 
                            'text-lg'
                          }`}
                          placeholder="Enter alt text..."
                        />
                        <div className="flex items-center justify-between">
                          <div className="text-xs text-gray-500">
                            Word count: {editedAltText.split(' ').filter(word => word.trim()).length} words
                          </div>
                          <div className="flex space-x-2">
                            <button
                              onClick={handleCancelEdit}
                              disabled={isSaving}
                              className="px-3 py-1 text-xs text-gray-600 border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50"
                            >
                              Cancel
                            </button>
                            <button
                              onClick={handleSaveAltText}
                              disabled={isSaving || !editedAltText.trim()}
                              className="px-3 py-1 text-xs text-white bg-blue-600 rounded hover:bg-blue-700 disabled:opacity-50"
                            >
                              {isSaving ? 'Saving...' : 'Save'}
                            </button>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div>
                        {image.alt_text ? (
                          <>
                            <div className="p-4 border rounded-md bg-gray-50">
                              <p className={`leading-relaxed text-gray-700 ${
                                textSize === 'small' ? 'text-sm' : 
                                textSize === 'medium' ? 'text-base' : 
                                'text-lg'
                              }`}>
                                {image.alt_text}
                              </p>
                            </div>
                            <div className="mt-2 text-xs text-gray-500">
                              Word count: {image.alt_text.split(' ').length} words
                            </div>
                          </>
                        ) : (
                          <div className="p-4 border border-gray-200 rounded-md bg-gray-50">
                            <p className="text-sm text-gray-500 italic">No alt text available</p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                {/* Tags */}
                {tags.length > 0 && (
                  <div>
                    <h4 className="mb-2 text-sm font-medium text-gray-900">Tags</h4>
                    <div className="flex flex-wrap gap-2">
                      {tags.map((tag, index) => (
                        <span key={index} className="px-2 py-1 text-xs text-blue-800 bg-blue-100 rounded-md">
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Error Message */}
                {image.error_message && (
                  <div>
                    <h4 className="mb-2 text-sm font-medium text-gray-900">Error Message</h4>
                    <div className="p-3 border border-red-200 rounded-md bg-red-50">
                      <p className="text-sm text-red-700">{image.error_message}</p>
                    </div>
                  </div>
                )}

                {/* Timestamps */}
                <div className="grid grid-cols-1 gap-4">
                  <div>
                    <h4 className="mb-2 text-sm font-medium text-gray-900">Timeline</h4>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-500">Uploaded:</span>
                        <span className="text-gray-700">{new Date(image.created_at).toLocaleString()}</span>
                      </div>
                      {image.processed_at && (
                        <div className="flex justify-between">
                          <span className="text-gray-500">Processed:</span>
                          <span className="text-gray-700">{new Date(image.processed_at).toLocaleString()}</span>
                        </div>
                      )}
                      {image.last_reprocessed_at && (
                        <div className="flex justify-between">
                          <span className="text-gray-500">Last Reprocessed:</span>
                          <span className="text-gray-700">{new Date(image.last_reprocessed_at).toLocaleString()}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="flex items-center justify-between pt-6 mt-6 border-t">
              <div className="text-sm text-gray-500">
                Image ID: {image.id}
              </div>
              <div className="flex space-x-3">
                <button onClick={handleClose} className="btn-outline">
                  Close
                </button>
                {(image.processing_status === 'completed' || image.processing_status === 'failed') && (
                  <button onClick={onReprocess} className="btn-primary">
                    Reprocess Image
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Full Size Image Preview Modal */}
      {showFullPreview && (
        <FullSizePreviewModal 
          batchId={batchId}
          image={image}
          onClose={() => setShowFullPreview(false)}
        />
      )}
    </>
  );
};

// Reprocess Modal Component (continues from Part 6)
const ReprocessModal = ({ image, onClose, onReprocess }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [comments, setComments] = useState('');
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  const handleClose = () => {
    setIsVisible(false);
    setTimeout(onClose, 300);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!comments.trim()) {
      toast.error('Please provide additional comments for reprocessing');
      return;
    }

    setProcessing(true);
    try {
      await onReprocess(image.id, comments.trim());
      handleClose();
    } finally {
      setProcessing(false);
    }
  };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  return (
    <div 
      className={`fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center p-4 transition-opacity duration-300 ${isVisible ? 'opacity-100' : 'opacity-0'}`}
      style={{ 
        zIndex: 9999,
        margin: 0,  // Remove any margins
        top: 0,     // Ensure it starts from the very top
        left: 0,
        right: 0,
        bottom: 0
      }}
      onClick={handleBackdropClick}
    >
      <div className={`bg-white rounded-lg shadow-xl max-w-lg w-full transform transition-all duration-300 ${isVisible ? 'scale-100 translate-y-0' : 'scale-95 translate-y-4'}`}>
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-medium text-gray-900">Reprocess Image</h3>
            <button
              onClick={handleClose}
              className="text-gray-400 hover:text-gray-500"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="space-y-4">
              <div>
                <label className="block mb-2 text-sm font-medium text-gray-700">
                  Image: {image.filename}
                </label>
              </div>

              <div>
                <label className="block mb-2 text-sm font-medium text-gray-700">
                  Additional Comments *
                </label>
                <textarea
                  value={comments}
                  onChange={(e) => setComments(e.target.value)}
                  rows={8}
                  className="input min-h-[160px]"
                  placeholder="Provide specific feedback or instructions for reprocessing this image..."
                  required
                />
                <p className="mt-1 text-xs text-gray-500">
                  Be specific about what needs to be improved or changed in the alt-text.
                </p>
              </div>

              {image.alt_text && (
                <div>
                  <label className="block mb-2 text-sm font-medium text-gray-700">
                    Current Alt Text:
                  </label>
                  <div className="p-3 rounded-md bg-gray-50">
                    <p className="text-sm text-gray-700">{image.alt_text}</p>
                  </div>
                </div>
              )}
            </div>

            <div className="flex justify-end pt-6 mt-6 space-x-3 border-t">
              <button
                type="button"
                onClick={handleClose}
                className="btn-outline"
                disabled={processing}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn-primary"
                disabled={processing || !comments.trim()}
              >
                {processing ? (
                  <span className="flex items-center">
                    <div className="w-4 h-4 mr-2 loading-spinner"></div>
                    Adding to Queue...
                  </span>
                ) : (
                  'Reprocess Image'
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

// Full Size Preview Modal Component
const FullSizePreviewModal = ({ batchId, image, onClose }) => {
  const [blobUrl, setBlobUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const loadFullSizeImage = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const token = Cookies.get('auth_token');
        if (!token) {
          throw new Error('Authentication token not found');
        }

        // Fetch image with proper auth headers
        const response = await fetch(`/api/batches/${batchId}/images/${image.id}/preview`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (!response.ok) {
          throw new Error(`Failed to load image: ${response.status} ${response.statusText}`);
        }

        // Convert to blob and create blob URL
        const blob = await response.blob();
        const url = URL.createObjectURL(blob);
        setBlobUrl(url);

      } catch (err) {
        console.error('❌ Full size image load error:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    loadFullSizeImage();

    // Cleanup blob URL when component unmounts
    return () => {
      if (blobUrl) {
        URL.revokeObjectURL(blobUrl);
      }
    };
  }, [batchId, image.id]);

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-90 z-[10000] !m-0 !p-0"
      onClick={handleBackdropClick}
      style={{ margin: 0, padding: 0 }}
    >
      <div className="absolute inset-0 flex items-center justify-center !m-0 !p-0">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-white hover:text-gray-300 z-[10001]"
        >
          <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
        
        <div className="max-w-[95vw] max-h-[95vh] !m-0 !p-0">
          {loading && (
            <div className="flex items-center justify-center min-w-[300px] min-h-[200px]">
              <div className="text-center text-white">
                <div className="w-8 h-8 mx-auto mb-2 loading-spinner border-white"></div>
                <span>Loading full size image...</span>
              </div>
            </div>
          )}
          
          {error && (
            <div className="flex items-center justify-center min-w-[300px] min-h-[200px]">
              <div className="text-center text-white">
                <PhotoIcon className="w-12 h-12 mx-auto mb-2 text-red-400" />
                <span>Failed to load image</span>
                <div className="mt-1 text-sm text-red-300">{error}</div>
              </div>
            </div>
          )}
          
          {blobUrl && !loading && !error && (
            <img
              src={blobUrl}
              alt={image.alt_text || 'Full size preview'}
              className="max-h-[95vh] max-w-[95vw] object-contain !m-0"
              onClick={(e) => e.stopPropagation()}
              style={{ margin: 0 }}
              onError={() => setError('Failed to display image')}
            />
          )}
        </div>
      </div>
    </div>
  );
};

// Sortable row used inside the Arrange Order modal
const SortableImageItem = ({ image, index, total, batchId, onMove, onMoveEdge }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable({ id: String(image.id) });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1
  };

  const iconBtn = 'p-1 text-gray-500 rounded hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed';

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="flex items-center gap-3 p-2 bg-white border border-gray-200 rounded-md"
    >
      <button
        {...attributes}
        {...listeners}
        className="p-1 text-gray-400 cursor-grab hover:text-gray-600 touch-none"
        title="Drag to reorder"
        type="button"
      >
        <Bars3Icon className="w-5 h-5" />
      </button>
      <span className="w-6 text-xs text-center text-gray-400">{index + 1}</span>
      <div className="flex-shrink-0 w-14 h-14 overflow-hidden bg-gray-100 rounded">
        <ImagePreview batchId={batchId} image={image} altText={image.filename} />
      </div>
      <span className="flex-1 text-sm text-gray-800 truncate" title={image.filename}>
        {image.filename}
      </span>
      <div className="flex items-center gap-0.5">
        <button type="button" onClick={() => onMoveEdge(index, 'top')} disabled={index === 0} className={iconBtn} title="Move to top">
          <ChevronDoubleUpIcon className="w-4 h-4" />
        </button>
        <button type="button" onClick={() => onMove(index, -1)} disabled={index === 0} className={iconBtn} title="Move up">
          <ArrowUpIcon className="w-4 h-4" />
        </button>
        <button type="button" onClick={() => onMove(index, 1)} disabled={index === total - 1} className={iconBtn} title="Move down">
          <ArrowDownIcon className="w-4 h-4" />
        </button>
        <button type="button" onClick={() => onMoveEdge(index, 'bottom')} disabled={index === total - 1} className={iconBtn} title="Move to bottom">
          <ChevronDoubleDownIcon className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

// Arrange Order modal: manual reorder (drag + buttons) with optional RTF quick-sort
const ArrangeOrderModal = ({ batchId, onClose, onSaved }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [importing, setImporting] = useState(false);
  const [report, setReport] = useState(null);
  const fileInputRef = useRef(null);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const loadAll = async () => {
    setLoading(true);
    try {
      // The list endpoint caps at 100 per page, so page through to get them all.
      let all = [];
      let page = 1;
      let hasNext = true;
      while (hasNext) {
        const res = await batchAPI.getImages(batchId, { page: String(page), limit: '100' });
        const data = res.data?.data || {};
        all = all.concat(data.images || []);
        hasNext = !!(data.pagination && data.pagination.hasNext);
        page += 1;
        if (page > 100) break; // safety valve
      }
      setItems(all);
    } catch (error) {
      console.error('Arrange: failed to load images', error);
      toast.error('Failed to load images');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAll();
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = 'unset'; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [batchId]);

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setItems((list) => {
      const oldIndex = list.findIndex(i => String(i.id) === String(active.id));
      const newIndex = list.findIndex(i => String(i.id) === String(over.id));
      if (oldIndex === -1 || newIndex === -1) return list;
      return arrayMove(list, oldIndex, newIndex);
    });
  };

  const move = (index, dir) => {
    setItems((list) => {
      const newIndex = index + dir;
      if (newIndex < 0 || newIndex >= list.length) return list;
      return arrayMove(list, index, newIndex);
    });
  };

  const moveToEdge = (index, edge) => {
    setItems((list) => arrayMove(list, index, edge === 'top' ? 0 : list.length - 1));
  };

  const handleImportRtf = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImporting(true);
    setReport(null);
    try {
      const res = await batchAPI.importSortRtf(batchId, file);
      setReport(res.data?.data || null);
      await loadAll(); // reflect the new server-side order
      toast.success(res.data?.message || 'Order imported from RTF');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to import RTF');
    } finally {
      setImporting(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const order = items.map(i => i.id);
      await batchAPI.updateSort(batchId, { mode: 'custom', order });
      toast.success('Custom order saved');
      onSaved();
    } catch (error) {
      toast.error('Failed to save order');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black bg-opacity-50"
      style={{ margin: 0 }}
    >
      <div className="flex flex-col bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh]">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b">
          <div>
            <h3 className="text-lg font-medium text-gray-900">Arrange custom order</h3>
            <p className="mt-1 text-sm text-gray-500">
              Drag rows or use the arrows. This order is used in the view and in downloads.
            </p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-500" title="Close">
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>

        {/* RTF import + report */}
        <div className="p-5 space-y-3 border-b bg-gray-50">
          <div className="flex flex-wrap items-center gap-3">
            <input
              ref={fileInputRef}
              type="file"
              accept=".rtf"
              onChange={handleImportRtf}
              className="hidden"
              id="rtf-sort-input"
            />
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={importing}
              className="flex items-center btn-outline btn-sm"
            >
              <DocumentArrowUpIcon className="w-4 h-4 mr-1" />
              {importing ? 'Importing…' : 'Import order from RTF'}
            </button>
            <span className="text-xs text-gray-500">
              Quick-sort by uploading the RTF (matched on File Name). You can still adjust afterward.
            </span>
          </div>

          {report && (
            <div className="p-3 text-sm bg-white border rounded-md">
              {report.allMatched ? (
                <p className="font-medium text-green-700">
                  All {report.matchedCount} image{report.matchedCount === 1 ? '' : 's'} were found and sorted.
                </p>
              ) : (
                <div className="space-y-2">
                  <p className="font-medium text-gray-800">
                    Matched {report.matchedCount} of {report.totalImages} image{report.totalImages === 1 ? '' : 's'}.
                  </p>
                  {report.missingInAltify?.length > 0 && (
                    <div>
                      <p className="text-xs font-semibold text-amber-700">
                        In the RTF but not in this batch ({report.missingInAltify.length}):
                      </p>
                      <ul className="ml-4 text-xs text-gray-600 list-disc max-h-24 overflow-auto">
                        {report.missingInAltify.map((n, i) => <li key={i}>{n}</li>)}
                      </ul>
                    </div>
                  )}
                  {report.missingInRtf?.length > 0 && (
                    <div>
                      <p className="text-xs font-semibold text-amber-700">
                        In this batch but not in the RTF ({report.missingInRtf.length}) — placed after matched, adjust below:
                      </p>
                      <ul className="ml-4 text-xs text-gray-600 list-disc max-h-24 overflow-auto">
                        {report.missingInRtf.map((n, i) => <li key={i}>{n}</li>)}
                      </ul>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Sortable list */}
        <div className="flex-1 p-5 overflow-auto">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="w-6 h-6 loading-spinner"></div>
            </div>
          ) : items.length === 0 ? (
            <p className="py-12 text-sm text-center text-gray-500">No images to arrange.</p>
          ) : (
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
              <SortableContext items={items.map(i => String(i.id))} strategy={verticalListSortingStrategy}>
                <div className="space-y-2">
                  {items.map((image, index) => (
                    <SortableImageItem
                      key={image.id}
                      image={image}
                      index={index}
                      total={items.length}
                      batchId={batchId}
                      onMove={move}
                      onMoveEdge={moveToEdge}
                    />
                  ))}
                </div>
              </SortableContext>
            </DndContext>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-5 border-t">
          <button onClick={onClose} className="btn-outline" disabled={saving}>
            Cancel
          </button>
          <button onClick={handleSave} className="btn-primary" disabled={saving || loading || items.length === 0}>
            {saving ? 'Saving…' : 'Save order'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default BatchDetailPage;